{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://raw.githubusercontent.com/jwildfire/safety.viz/main/src/data/schema/delta-delta.json",
  "title": "safety.viz delta-delta data contract",
  "description": "Long-format results data: one record per measurement at a visit (SDD-DATA-001). Column names are supplied by the settings mapping; each participant needs at least a baseline and a comparison visit for the two selected measures so change-from-baseline can be computed. The renderer removes missing/non-numeric results with a reported count (SDD-REG-008) and plots one point per participant (change in measure X vs change in measure Y).",
  "type": "object",
  "required": ["data", "settings"],
  "properties": {
    "data": {
      "type": "array",
      "minItems": 1,
      "items": { "type": "object" },
      "description": "d3.csv()-style records; every row carries the measure, result, participant, and visit columns named in settings."
    },
    "settings": {
      "type": "object",
      "description": "Column mappings and rendering options; merged onto the module's DEFAULT_SETTINGS, so only overrides need to be supplied.",
      "required": ["measure_col", "value_col", "id_col", "visit_col"],
      "properties": {
        "measure_col": {
          "type": "string",
          "default": "TEST",
          "description": "Column holding the measure name; required in data (SDD-CFG-004)."
        },
        "value_col": {
          "type": "string",
          "default": "STRESN",
          "description": "Column holding the numeric result; required in data. Non-numeric results are removed with a logged count (SDD-CFG-005, SDD-REG-008)."
        },
        "id_col": {
          "type": "string",
          "default": "USUBJID",
          "description": "Participant identifier column; one plotted point per participant (SDD-CFG-006)."
        },
        "visit_col": {
          "type": "string",
          "default": "VISIT",
          "description": "Categorical visit column; drives the baseline/comparison visit selectors (SDD-CFG-007)."
        },
        "visitn_col": {
          "type": "string",
          "default": "VISITNUM",
          "description": "Optional numeric visit column; orders the visit selectors and the sparkline (SDD-CFG-008)."
        },
        "measure_x": {
          "type": ["string", "null"],
          "default": null,
          "description": "Measure plotted on the x-axis; defaults to the first measure in the data (SDD-CFG-009, SDD-CFG-010)."
        },
        "measure_y": {
          "type": ["string", "null"],
          "default": null,
          "description": "Measure plotted on the y-axis; defaults to the second measure in the data (SDD-CFG-011)."
        },
        "baseline_visits": {
          "type": "array",
          "items": { "type": "string" },
          "default": [],
          "description": "Baseline visit(s); multiple visits are averaged. Defaults to the first visit (SDD-CFG-012)."
        },
        "comparison_visits": {
          "type": "array",
          "items": { "type": "string" },
          "default": [],
          "description": "Comparison visit(s); multiple visits are averaged. Defaults to the last visit (SDD-CFG-013)."
        },
        "add_regression_line": {
          "type": "boolean",
          "default": true,
          "description": "Draw a simple linear regression line with an equation and R² note (SDD-REG-026)."
        },
        "measures": {
          "type": ["array", "null"],
          "items": { "type": "string" },
          "default": null,
          "description": "Ordered whitelist of measures the Measure control offers: only these appear, and in this order. null or [] offers every measure in the data, alphabetically. The first two listed are the comparison the chart opens on unless measure_x / measure_y say otherwise. Configured measures absent from the data are dropped with a console warning (SDD-MEAS-001)."
        },
        "filters": {
          "$ref": "#/$defs/filterList",
          "description": "Optional filter columns rendered as controls (SDD-CFG-014). Each entry is a column name or a { value_col, label, start, all, multiple } spec: `start` is the value the filter opens on, `all` (default true) is whether an \"All\" option is offered, and `multiple` (default false) makes it a checkbox multiselect."
        },
        "details": {
          "$ref": "#/$defs/fieldList",
          "description": "Optional participant-detail columns shown above the linked measure table; defaults derive from id_col and the filters (SDD-CFG-015)."
        }
      }
    }
  },
  "$defs": {
    "fieldList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" }
            }
          }
        ]
      }
    },
    "filterList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" },
              "start": {
                "type": ["string", "number", "boolean", "array", "null"],
                "default": null,
                "description": "The value the filter opens on; an array of values for a `multiple` filter. 0 and false are real values, not an absent start. A value the data lacks is dropped with a console warning."
              },
              "all": {
                "type": "boolean",
                "default": true,
                "description": "Whether the filter offers an \"All\" option. false removes it, and the filter then always holds one value: the first, unless `start` names another."
              },
              "multiple": {
                "type": "boolean",
                "default": false,
                "description": "Render a checkbox multiselect instead of a dropdown; the rows shown are those matching any selected value."
              }
            }
          }
        ]
      }
    }
  }
}
